import * as React from 'react';
import RcTooltip from '@rc-component/tooltip';
import { useControlledState } from '@rc-component/util';
import { clsx } from 'clsx';
import ContextIsolator from '../_util/ContextIsolator';
import { useZIndex } from '../_util/hooks';
import { useMergeSemantic, useSemanticRootStyle } from '../_util/hooks/useMergeSemantic';
import { isFunction } from '../_util/is';
import { getTransitionName } from '../_util/motion';
import getPlacements from '../_util/placements';
import { cloneElement, isFragment } from '../_util/reactNode';
import { devUseWarning } from '../_util/warning';
import ZIndexContext from '../_util/zindexContext';
import { useComponentConfig } from '../config-provider/context';
import useCSSVarCls from '../config-provider/hooks/useCSSVarCls';
import TableMeasureRowContext from '../table/TableMeasureRowContext';
import { useToken } from '../theme/internal';
import useMergedArrow from './hook/useMergedArrow';
import PurePanel from './PurePanel';
import useStyle from './style';
import UniqueProvider from './UniqueProvider';
import { parseColor } from './util';
const InternalTooltip = React.forwardRef((props, ref) => {
    const { prefixCls: customizePrefixCls, openClassName, getTooltipContainer, color, children, afterOpenChange, arrow: tooltipArrow, destroyTooltipOnHide, destroyOnHidden, title, overlay, trigger, builtinPlacements, autoAdjustOverflow = true, motion, getPopupContainer, placement = 'top', mouseEnterDelay, mouseLeaveDelay, rootClassName, styles, classNames, onOpenChange, 
    // Legacy
    overlayInnerStyle, overlayStyle, overlayClassName, ...restProps } = props;
    const [, token] = useToken();
    const injectFromPopover = props['data-popover-inject'];
    const { getPopupContainer: getContextPopupContainer, getPrefixCls, direction, ...semanticConfig } = useComponentConfig('tooltip');
    // When injected from Popover/Popconfirm, skip tooltip-specific semantic config
    // to prevent ConfigProvider tooltip config from leaking into those components
    const { className: contextClassName, style: contextStyle, classNames: contextClassNames, styles: contextStyles, arrow: contextArrow, trigger: contextTrigger, mouseEnterDelay: contextMouseEnterDelay, mouseLeaveDelay: contextMouseLeaveDelay, } = injectFromPopover ? {} : semanticConfig;
    const mergedMouseEnterDelay = mouseEnterDelay ?? contextMouseEnterDelay ?? 0.1;
    const mergedMouseLeaveDelay = mouseLeaveDelay ?? contextMouseLeaveDelay ?? 0.1;
    const mergedArrow = useMergedArrow(tooltipArrow, contextArrow);
    const mergedShowArrow = mergedArrow.show;
    const mergedTrigger = trigger || contextTrigger || 'hover';
    const mergedGetPopupContainer = getPopupContainer || getContextPopupContainer;
    const mergedDestroyOnHidden = destroyOnHidden ?? !!destroyTooltipOnHide;
    const inTableMeasureRow = React.useContext(TableMeasureRowContext);
    // ============================== Ref ===============================
    const warning = devUseWarning('Tooltip');
    const tooltipRef = React.useRef(null);
    const forceAlign = () => {
        tooltipRef.current?.forceAlign();
    };
    React.useImperativeHandle(ref, () => ({
        forceAlign,
        nativeElement: tooltipRef.current?.nativeElement,
        popupElement: tooltipRef.current?.popupElement,
    }));
    // ============================== Warn ==============================
    if (process.env.NODE_ENV !== 'production') {
        [
            ['overlayStyle', 'styles.root'],
            ['overlayInnerStyle', 'styles.container'],
            ['overlayClassName', 'classNames.root'],
            ['destroyTooltipOnHide', 'destroyOnHidden'],
        ].forEach(([deprecatedName, newName]) => {
            warning.deprecated(!(deprecatedName in props), deprecatedName, newName);
        });
        warning(!destroyTooltipOnHide || typeof destroyTooltipOnHide === 'boolean', 'usage', '`destroyTooltipOnHide` no need config `keepParent` anymore. Please use `boolean` value directly.');
    }
    // ============================== Open ==============================
    const [open, setOpen] = useControlledState(props.defaultOpen ?? false, props.open);
    const noTitle = !title && !overlay && title !== 0; // overlay for old version compatibility
    const onInternalOpenChange = (nextOpen) => {
        setOpen(noTitle ? false : nextOpen);
        if (!noTitle && onOpenChange) {
            onOpenChange(nextOpen);
        }
    };
    const tooltipPlacements = React.useMemo(() => {
        return (builtinPlacements ||
            getPlacements({
                arrowPointAtCenter: mergedArrow?.pointAtCenter ?? false,
                autoAdjustOverflow,
                arrowWidth: mergedShowArrow ? token.sizePopupArrow : 0,
                borderRadius: token.borderRadius,
                offset: token.marginXXS,
                visibleFirst: true,
            }));
    }, [mergedArrow, builtinPlacements, token, mergedShowArrow, autoAdjustOverflow]);
    const memoOverlay = React.useMemo(() => {
        if (title === 0) {
            return title;
        }
        return overlay || title || '';
    }, [overlay, title]);
    const memoOverlayWrapper = (React.createElement(ContextIsolator, { space: true, form: true }, isFunction(memoOverlay) ? memoOverlay() : memoOverlay));
    // =========== Merged Props for Semantic ===========
    const mergedProps = {
        ...props,
        trigger: mergedTrigger,
        builtinPlacements: tooltipPlacements,
        getPopupContainer: mergedGetPopupContainer,
        destroyOnHidden: mergedDestroyOnHidden,
        mouseEnterDelay: mergedMouseEnterDelay,
        mouseLeaveDelay: mergedMouseLeaveDelay,
    };
    const contextStyleRoot = useSemanticRootStyle(contextStyle);
    const overlayStyleRoot = useSemanticRootStyle(overlayStyle);
    const [mergedClassNames, mergedStyles] = useMergeSemantic([contextClassNames, classNames], [contextStyles, contextStyleRoot, styles, overlayStyleRoot], {
        props: mergedProps,
    });
    const prefixCls = getPrefixCls('tooltip', customizePrefixCls);
    const rootPrefixCls = getPrefixCls();
    let tempOpen = open;
    // Hide tooltip when there is no title or in table measure row
    if ((!('open' in props) && noTitle) || inTableMeasureRow) {
        tempOpen = false;
    }
    // ============================= Render =============================
    const child = React.isValidElement(children) && !isFragment(children) ? children : React.createElement("span", null, children);
    const childProps = child.props;
    const childCls = !childProps.className || typeof childProps.className === 'string'
        ? clsx(childProps.className, openClassName || `${prefixCls}-open`)
        : childProps.className;
    // Style
    const rootCls = useCSSVarCls(prefixCls);
    const [hashId, cssVarCls] = useStyle(prefixCls, rootCls, !injectFromPopover);
    // Color
    const colorInfo = parseColor(rootPrefixCls, prefixCls, color);
    const arrowContentStyle = colorInfo.arrowStyle;
    const themeCls = clsx(rootCls, hashId, cssVarCls);
    const rootClassNames = clsx(overlayClassName, { [`${prefixCls}-rtl`]: direction === 'rtl' }, colorInfo.className, rootClassName, themeCls, contextClassName, mergedClassNames.root);
    // ============================ zIndex ============================
    const [zIndex, contextZIndex] = useZIndex('Tooltip', restProps.zIndex);
    const containerStyle = {
        ...mergedStyles.container,
        ...overlayInnerStyle,
        ...colorInfo.overlayStyle,
    };
    const content = (React.createElement(RcTooltip, { unique: true, ...restProps, zIndex: zIndex, showArrow: mergedShowArrow, placement: placement, mouseEnterDelay: mergedMouseEnterDelay, mouseLeaveDelay: mergedMouseLeaveDelay, prefixCls: prefixCls, classNames: {
            root: rootClassNames,
            container: mergedClassNames.container,
            arrow: mergedClassNames.arrow,
            uniqueContainer: clsx(themeCls, mergedClassNames.container),
        }, styles: {
            root: {
                ...arrowContentStyle,
                ...mergedStyles.root,
            },
            container: containerStyle,
            uniqueContainer: containerStyle,
            arrow: mergedStyles.arrow,
        }, ref: tooltipRef, overlay: memoOverlayWrapper, visible: tempOpen, onVisibleChange: onInternalOpenChange, afterVisibleChange: afterOpenChange, arrowContent: React.createElement("span", { className: `${prefixCls}-arrow-content` }), motion: {
            motionName: getTransitionName(rootPrefixCls, 'zoom-big-fast', typeof motion?.motionName === 'string' ? motion?.motionName : undefined),
            motionDeadline: 1000,
        }, trigger: mergedTrigger, builtinPlacements: tooltipPlacements, getTooltipContainer: mergedGetPopupContainer, destroyOnHidden: mergedDestroyOnHidden }, tempOpen && !restProps.disabled ? cloneElement(child, { className: childCls }) : child));
    return React.createElement(ZIndexContext.Provider, { value: contextZIndex }, content);
});
const Tooltip = InternalTooltip;
if (process.env.NODE_ENV !== 'production') {
    Tooltip.displayName = 'Tooltip';
}
Tooltip._InternalPanelDoNotUseOrYouWillBeFired = PurePanel;
Tooltip.UniqueProvider = UniqueProvider;
export default Tooltip;
